import React, { createContext, useCallback, useContext, useMemo, useRef, useState, useLayoutEffect } from 'react'

interface SlicersContextValue {
  selections: Record<string, string[]>
  registerSlicer: (sourceColumnId: string) => string[]
  setSlicer: (sourceColumnId: string, values: string[]) => void
}

const DEFAULT_VALUE: SlicersContextValue = {
  selections: {},
  registerSlicer: () => [],
  setSlicer: () => undefined
}

const SlicersContext = createContext<SlicersContextValue>(DEFAULT_VALUE)

interface SlicersProviderProps {
  processId: string
  children: React.ReactNode
}

// Slicer selections are intentionally in-memory only — never persisted to localStorage. Persisting
// per-user filters added fragility for little product value: a stored value that later becomes
// deprecated (the option no longer exists in the source data) would keep silently filtering out
// rows the user expects to see. Selections now reset every session/reload; any leftover
// `slicer_*` entries from before this change are simply ignored (see issue #10380).
export const SlicersProvider: React.FC<SlicersProviderProps> = ({ processId, children }) => {
  const [selections, setSelections] = useState<Record<string, string[]>>({})

  // Reset selections whenever the document changes. The provider instance is reused across
  // document navigation (the route is not keyed by processId), so without this an in-memory
  // selection from one document would leak into the next and filter its views.
  const prevProcessId = useRef(processId)

  useLayoutEffect(() => {
    if (prevProcessId.current !== processId) {
      prevProcessId.current = processId
      setSelections({})
    }
  }, [processId])

  const registerSlicer = useCallback((sourceColumnId: string): string[] => {
    setSelections((prev) => (prev[sourceColumnId] === undefined ? { ...prev, [sourceColumnId]: [] } : prev))
    return []
  }, [])

  const setSlicer = useCallback((sourceColumnId: string, values: string[]) => {
    setSelections((prev) => ({ ...prev, [sourceColumnId]: values }))
  }, [])

  const value = useMemo<SlicersContextValue>(
    () => ({ selections, registerSlicer, setSlicer }),
    [selections, registerSlicer, setSlicer]
  )

  return <SlicersContext.Provider value={value}>{children}</SlicersContext.Provider>
}

export const useSlicers = (): SlicersContextValue => useContext(SlicersContext)

export default SlicersContext
